import { Card, CardContent, Typography, Box, Tooltip, Chip } from '@mui/material';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, getDay } from 'date-fns';
import { da } from 'date-fns/locale';
import type { CalendarEntry } from '../../types';

interface UtilizationCalendarProps {
  data: Record<string, CalendarEntry[]>;
  month: Date;
  trailers?: Array<{ id: string; number: string }>;
  onDayClick?: (date: string, trailerId: string) => void;
}

const WEEKDAYS = ['Man', 'Tir', 'Ons', 'Tor', 'Fre', 'Lør', 'Søn'];

export default function UtilizationCalendar({
  data,
  month,
  trailers: _trailers,
  onDayClick,
}: UtilizationCalendarProps) {
  const days = eachDayOfInterval({
    start: startOfMonth(month),
    end: endOfMonth(month),
  });

  // Get the first day of the month (0 = Sunday, 1 = Monday, etc.)
  const firstDayOfMonth = getDay(startOfMonth(month));
  // Adjust for Monday start (0 = Monday)
  const startOffset = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;

  const getUtilizationColor = (entries: CalendarEntry[] | undefined) => {
    if (!entries || entries.length === 0) return 'grey.200';
    const activeCount = entries.filter(e => e.isActive).length;
    const rate = activeCount / entries.length;
    if (rate >= 0.8) return 'success.light';
    if (rate >= 0.5) return 'warning.light';
    if (rate > 0) return 'warning.main';
    return 'grey.300';
  };

  const getUtilizationLabel = (entries: CalendarEntry[] | undefined) => {
    if (!entries || entries.length === 0) return 'Ingen data';
    const activeCount = entries.filter(e => e.isActive).length;
    return `${activeCount}/${entries.length} aktive`;
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            {format(month, 'MMMM yyyy', { locale: da })}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Chip size="small" sx={{ bgcolor: 'success.light' }} label="Høj" />
            <Chip size="small" sx={{ bgcolor: 'warning.light' }} label="Medium" />
            <Chip size="small" sx={{ bgcolor: 'grey.300' }} label="Lav" />
          </Box>
        </Box>

        {/* Weekday headers */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, mb: 0.5 }}>
          {WEEKDAYS.map((day) => (
            <Box
              key={day}
              sx={{
                textAlign: 'center',
                py: 0.5,
                fontSize: 12,
                fontWeight: 600,
                color: 'text.secondary',
              }}
            >
              {day}
            </Box>
          ))}
        </Box>

        {/* Calendar grid */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
          {/* Empty cells for offset */}
          {Array.from({ length: startOffset }).map((_, i) => (
            <Box key={`empty-${i}`} sx={{ aspectRatio: '1', p: 0.5 }} />
          ))}

          {/* Day cells */}
          {days.map((day) => {
            const dateStr = format(day, 'yyyy-MM-dd');
            const entries = data[dateStr];

            return (
              <Tooltip
                key={dateStr}
                title={getUtilizationLabel(entries)}
                arrow
              >
                <Box
                  onClick={() => onDayClick?.(dateStr, '')}
                  sx={{
                    aspectRatio: '1',
                    p: 0.5,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: getUtilizationColor(entries),
                    borderRadius: 1,
                    cursor: onDayClick ? 'pointer' : 'default',
                    '&:hover': {
                      opacity: 0.8,
                    },
                  }}
                >
                  <Typography
                    variant="body2"
                    fontWeight={500}
                    sx={{ color: 'text.primary' }}
                  >
                    {format(day, 'd')}
                  </Typography>
                </Box>
              </Tooltip>
            );
          })}
        </Box>
      </CardContent>
    </Card>
  );
}
